import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Compact button treatment for expandable rows and panels.
 *
 * Variants:
 *   - `row` — full-width row trigger.
 *   - `nav` — panel/list navigation trigger.
 */
const disclosureButtonVariants = cva("cw-v2-disclosure-button", {
  variants: {
    variant: {
      row: "cw-v2-disclosure-button--row",
      nav: "cw-v2-disclosure-button--nav",
    },
  },
  defaultVariants: {
    variant: "row",
  },
});

export type DisclosureButtonVariant = NonNullable<
  VariantProps<typeof disclosureButtonVariants>["variant"]
>;

export interface DisclosureButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof disclosureButtonVariants> {
  /** Reflected as `aria-expanded`. */
  open?: boolean;
  /** Marks `data-active` for nav-style triggers. */
  active?: boolean;
}

export const DisclosureButton = React.forwardRef<HTMLButtonElement, DisclosureButtonProps>(
  function DisclosureButton({ active, className, open, type = "button", variant, ...props }, ref) {
    return (
      <button
        ref={ref}
        aria-expanded={open}
        className={cx(disclosureButtonVariants({ variant }), className)}
        data-active={active ? true : undefined}
        type={type}
        {...props}
      />
    );
  },
);
DisclosureButton.displayName = "DisclosureButton";
